// @file Composable for handling whiteboard actions like cut, copy, paste, group, etc.

import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { exportPollResultsAsCSV } from '@@/bits/surface_polls'
import { useTldrawMenuClipboardEvents } from '@@/bits/tldraw/tldraw_hooks'
import type { ActionSource } from '@@/bits/whiteboard'
import { ActionType } from '@@/bits/whiteboard'
import { applyGroupLinking } from '@@/bits/whiteboard_linking'
import { applyGroupSpecialEffects } from '@@/bits/whiteboard_special_effects'
import { SELECT_TOOL } from '@@/bits/whiteboard_tools'
import postMessage from '@@/native_bridge/post_message'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardElementModifierStore } from '@@/pinia/whiteboard_element_modifier'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useClipboardItems } from '@@/vuecomposables/useClipboardItems'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardClipboardPosition } from '@@/vuecomposables/useWhiteboardClipboardPosition'
import { useWhiteboardFrameActions } from '@@/vuecomposables/use_whiteboard_frame_actions'
import { useWhiteboardShapeActions } from '@@/vuecomposables/use_whiteboard_shape_actions'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { TLShape } from 'tldraw'
import { createShapeId } from 'tldraw'

/**
 * Provides context to `trackEvent` when pasting or duplicating shapes to know how much of each type are pasted or duplicated.
 */
const getShapeActionProperties = (
  shapes: TLShape[],
): {
  quantity: number
  shapeTypes: Record<string, number>
  shapeType: string
} | null => {
  if (shapes.length === 0) {
    return null
  }

  const shapeTypes = shapes.map((shape) => {
    if (shape.type === 'padlet-attachment-shape') {
      const contentSource = (shape as PadletAttachmentShape).meta.contentSource
      const sourceString = typeof contentSource === 'string' ? contentSource : 'unknown'
      return `padlet-attachment-${sourceString}`
    }
    return shape.type
  })
  const shapeTypeCounts = shapeTypes.reduce<Record<string, number>>((acc, type) => {
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {})

  return {
    quantity: shapes.length,
    shapeTypes: shapeTypeCounts,
    shapeType: Object.keys(shapeTypeCounts).length > 1 ? 'multiple' : Object.keys(shapeTypeCounts)[0] || 'unknown',
  }
}

const useWhiteboardActions = (): {
  handleAction: typeof handleAction
} => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const whiteboardStore = useWhiteboardStore()
  const whiteboardElementModifierStore = useWhiteboardElementModifierStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const { updateShapesTextAlignment, copyShapes, pasteShapes } = useWhiteboardShapeActions()

  const handleAction = async (
    actionType: ActionType,
    selectedShapes?: TLShape[],
    source?: ActionSource,
  ): Promise<void> => {
    const { editor } = useTldrawEditor()
    if (editor == null) return

    selectedShapes ??= whiteboardElementModifierStore.selectedShapes

    // We only allow actions on the least permissive of the selected objects
    const areAllSelectedShapesEditable = selectedShapes.every(whiteboardPermissionsStore.canIEditShapeInFrame)

    switch (actionType) {
      case ActionType.Undo: {
        if (whiteboardStore.canIUndo) {
          trackEvent('Whiteboard', 'Undo action')
          editor.undo()
        }
        break
      }
      case ActionType.Redo: {
        if (whiteboardStore.canIRedo) {
          trackEvent('Whiteboard', 'Redo action')
          editor.redo()
        }
        break
      }
      case ActionType.SendToFront: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        trackEvent('Whiteboard', 'Send to front')
        editor.markHistoryStoppingPoint('bring to front')
        editor.bringToFront(selectedShapes)
        break
      }
      case ActionType.SendToBack: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        trackEvent('Whiteboard', 'Send to back')
        editor.markHistoryStoppingPoint('send to back')
        editor.sendToBack(selectedShapes)
        break
      }
      case ActionType.Group: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        trackEvent('Whiteboard', 'Group objects')
        const groupShapesMark = editor.markHistoryStoppingPoint('group shapes')
        if (groupShapesMark == null) return

        // Create group with explicit ID so we can access it immediately
        const groupId = createShapeId()
        editor.groupShapes(selectedShapes, { groupId })

        // Now we can directly access the created group shape
        const groupShape = editor.getShape(groupId)
        if (groupShape) {
          // Apply linking synchronously to the created group
          applyGroupLinking(selectedShapes, groupShape, groupShapesMark)

          // Apply special effects synchronously to the created group
          applyGroupSpecialEffects(selectedShapes, groupShape, groupShapesMark)
        }

        editor.squashToMark(groupShapesMark)
        break
      }
      case ActionType.Ungroup: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        const ungroupShapesMark = editor.markHistoryStoppingPoint('ungroup shapes')
        if (ungroupShapesMark == null) return
        trackEvent('Whiteboard', 'Ungroup objects')
        editor.ungroupShapes(selectedShapes)
        editor.squashToMark(ungroupShapesMark)
        break
      }
      case ActionType.Cut: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        trackEvent('Whiteboard', 'Cut objects')
        editor.markHistoryStoppingPoint('cut')
        const { setClipboardSource } = useWhiteboardClipboardPosition()

        void useTldrawMenuClipboardEvents().cut('unknown')
        setClipboardSource()
        break
      }
      case ActionType.Copy: {
        if (selectedShapes.length > 0) {
          editor.markHistoryStoppingPoint('copy')
          const success = await copyShapes(selectedShapes)
          if (success) {
            trackEvent('Whiteboard', 'Copied objects', null, null, getShapeActionProperties(selectedShapes))
          }
        } else {
          // Implicitly, copy a frame if no shapes are selected
          if (!isAppUsing('sandboxCopyPasteFrames')) {
            return
          }
          if (whiteboardStore.currentFrameId) {
            const success = await whiteboardStore.copyFrame(whiteboardStore.currentFrameId)
            if (success) {
              trackEvent('Whiteboard', 'Copy frame', null, null, { context: source ?? 'Unknown' })
              globalSnackbarStore.setSnackbar({
                message: __('Card copied'),
                notificationType: SnackbarNotificationType.success,
                timeout: 1500,
              })
            } else {
              globalSnackbarStore.setSnackbar({
                message: __('Failed to copy card'),
                notificationType: SnackbarNotificationType.error,
                timeout: 3000,
              })
            }
          }
        }
        break
      }

      case ActionType.Paste: {
        // Safari restricts clipboard access to one read per user gesture/synchronous operation.
        // Multiple clipboard.read() calls within the same paste action can fail in Safari,
        // so we read once and reuse the clipboard items for both frame detection and shape pasting.
        const { readClipboardItems, getClipboardFrameId } = useClipboardItems()
        const clipboardItems = await readClipboardItems()

        if (!clipboardItems) {
          return
        }

        if (isAppUsing('sandboxCopyPasteFrames')) {
          // When flag is enabled, check for frame ID in clipboard items
          const sourceFrameId = await getClipboardFrameId(clipboardItems)

          if (sourceFrameId) {
            const success = await whiteboardStore.pasteFrame(sourceFrameId)
            if (success) {
              trackEvent('Whiteboard', 'Paste frame', null, null, { context: source ?? 'Unknown' })
            }
            break
          }
        }

        // Regular shape paste (flag disabled OR no frame found when flag enabled)
        trackEvent('Whiteboard', 'Paste objects', null, null, getShapeActionProperties(selectedShapes))

        if (isAppCapableOf('nativeWhiteboardPaste')) {
          postMessage({ message_type: 'native_whiteboard_paste' })
          break
        }

        if (source === 'context-menu') {
          const pastePoint = editor.inputs.currentPagePoint
          await useTldrawMenuClipboardEvents().paste(clipboardItems, 'unknown', pastePoint)
          // Handle attachment shape deep copying after paste
          await pasteShapes()
        } else {
          const { setClipboardSource, getPastePoint } = useWhiteboardClipboardPosition()
          const pastePoint = getPastePoint()
          await useTldrawMenuClipboardEvents().paste(clipboardItems, 'unknown', pastePoint)
          setClipboardSource()
          // Handle attachment shape deep copying after paste
          await pasteShapes()
        }

        break
      }
      case ActionType.Duplicate: {
        if (selectedShapes.length === 0) {
          return
        }

        const deepCopyShapesResult = await whiteboardStore.deepCopyShapes(selectedShapes.map((shape) => shape.id))

        if (deepCopyShapesResult !== false) {
          trackEvent('Whiteboard', 'Duplicate objects', null, null, getShapeActionProperties(selectedShapes))
          editor.markHistoryStoppingPoint('duplicate shapes')
          editor.duplicateShapes(selectedShapes, { x: 20, y: 20 })

          const duplicatedShapes = editor.getSelectedShapes() ?? []

          whiteboardStore.updateCopiedShapes(deepCopyShapesResult, duplicatedShapes)
        }

        break
      }
      case ActionType.ToggleFreeze: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        trackEvent('Whiteboard', 'Toggle freeze')
        editor.markHistoryStoppingPoint('toggle freeze')
        editor.toggleLock(selectedShapes)
        break
      }
      case ActionType.Delete: {
        if (selectedShapes.length === 0) {
          return
        }
        if (!areAllSelectedShapesEditable) {
          return
        }

        trackEvent('Whiteboard', 'Delete objects')
        editor.run(
          () => {
            editor.markHistoryStoppingPoint('delete')
            editor.deleteShapes(selectedShapes ?? [])
            editor.selectNone()
            // `editor.selectNone` in theory should trigger a reaction to clear selected shapes,
            // but not sure why it doesn't work -> set `selectedShapes` to empty array explicitly here.
            whiteboardElementModifierStore.selectedShapes = []
          },
          { ignoreShapeLock: true },
        )
        break
      }
      case ActionType.SelectAll: {
        trackEvent('Whiteboard', 'Select all')
        if (isAppUsing('sandboxRetainActiveTool')) {
          whiteboardStore.selectTool(SELECT_TOOL.id)
          editor.setCurrentTool(SELECT_TOOL.tldrawToolId)
        }
        editor.markHistoryStoppingPoint('select all')
        editor.selectAll()
        break
      }
      case ActionType.GoToPreviousPage: {
        trackEvent('Whiteboard', 'Go to previous page')
        // Use whiteboardStore.frames (filtered) instead of editor.getPages() to skip admin-only frames in play mode
        const currentPageId = editor.getCurrentPageId()
        const visibleFrames = whiteboardStore.frames
        const currentIndex = visibleFrames.findIndex((f) => f.id === currentPageId)
        const previousFrame = currentIndex > 0 ? visibleFrames[currentIndex - 1] : undefined
        if (previousFrame == null) return
        useWhiteboardFrameActions().switchFrame(previousFrame.id)
        break
      }
      case ActionType.GoToNextPage: {
        trackEvent('Whiteboard', 'Go to next page')
        // Use whiteboardStore.frames (filtered) instead of editor.getPages() to skip admin-only frames in play mode
        const currentPageId = editor.getCurrentPageId()
        const visibleFrames = whiteboardStore.frames
        const currentIndex = visibleFrames.findIndex((f) => f.id === currentPageId)
        const nextFrame = currentIndex < visibleFrames.length - 1 ? visibleFrames[currentIndex + 1] : undefined
        if (nextFrame == null) return
        useWhiteboardFrameActions().switchFrame(nextFrame.id)
        break
      }
      case ActionType.EditPoll: {
        if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
          return
        }

        const shape = selectedShapes[0] as PadletAttachmentShape
        const isPoll = shape.props.isPoll
        const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
        if (isPoll && post != null) {
          trackEvent('Whiteboard', 'Edit poll')
          useSurfacePostsStore().startEditingPost({ postCid: post.cid })
          useSurfaceContentPickerStore().showPostContentPicker()
          useContentPickerStore().pickContentSource({ source: ContentPickerSource.Poll })
        }
        break
      }
      case ActionType.OpenPoll: {
        if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
          return
        }
        const shape = selectedShapes[0] as PadletAttachmentShape
        const isPoll = shape.props.isPoll
        const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
        if (isPoll && post != null) {
          trackEvent('Whiteboard', 'Open poll')
          void useSurfacePostActionStore().reopenPoll({ post })
        }
        break
      }
      case ActionType.ClosePoll: {
        if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
          return
        }
        const shape = selectedShapes[0] as PadletAttachmentShape
        const isPoll = shape.props.isPoll
        const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
        if (isPoll && post != null) {
          trackEvent('Whiteboard', 'Close poll')
          void useSurfacePostActionStore().closePoll({ post })
        }
        break
      }
      case ActionType.ExportPoll: {
        if (!areAllSelectedShapesEditable || selectedShapes.length !== 1) {
          return
        }
        const shape = selectedShapes[0] as PadletAttachmentShape
        const isPoll = shape.props.isPoll
        const post = useSurfacePostsStore()?.postEntitiesById[shape.props.postId ?? '']
        if (isPoll && post != null) {
          trackEvent('Whiteboard', 'Export poll results as CSV')
          void exportPollResultsAsCSV(useSurfaceStore().publicKey, post)
        }
        break
      }
      case ActionType.TextAlignLeft: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        const didUpdate = updateShapesTextAlignment(selectedShapes, 'start')
        if (didUpdate) {
          trackEvent('Whiteboard', 'Align text left')
          editor.markHistoryStoppingPoint('text align left')
        }
        break
      }
      case ActionType.TextAlignCenter: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        const didUpdate = updateShapesTextAlignment(selectedShapes, 'middle')
        if (didUpdate) {
          trackEvent('Whiteboard', 'Align text center')
          editor.markHistoryStoppingPoint('text align center')
        }
        break
      }
      case ActionType.TextAlignRight: {
        if (!areAllSelectedShapesEditable) {
          return
        }
        const didUpdate = updateShapesTextAlignment(selectedShapes, 'end')
        if (didUpdate) {
          trackEvent('Whiteboard', 'Align text right')
          editor.markHistoryStoppingPoint('text align right')
        }
        break
      }
      default:
        break
    }
  }

  return {
    handleAction,
  }
}

export { useWhiteboardActions }
